import { ApplicationConfig, inject, provideAppInitializer, importProvidersFrom, provideZonelessChangeDetection, isDevMode } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { RouteReuseStrategy } from '@angular/router';
import { ActionReducer, MetaReducer, provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { NgxPermissionsModule } from 'ngx-permissions';
import { AuthClientConfig, provideAuth0 } from '@auth0/auth0-angular';
import { provideTranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { localStorageSync } from 'ngrx-store-localstorage';
import { PharRouteReuseStrategy } from './app-routing/route-reuse-strategy';

// Import store configuration
import { reducers } from './store/reducers';
import { AppState } from './store/models/app.state';

// Import all effects
import { UserEffects } from './user/store/user.effects';
import { ProjectEffect } from './project/store/project.effect';
import { FormEffects } from './form/store/form.effects';
import { QuestionEffects } from './question/store/question.effects';
import { AssignEffects } from './assign/store/assign.effect';
import { AuditEffects } from './audit/store/audit.effects';
import { EventEffects } from './events/store/event.effects';
import { DatasetEffect } from './dataset/store/dataset.effect';
import { SettingsEffects } from './store/effects/settings.effects';
import { SiteEffects } from './site/store/site.effects';
import { RolesAndPermissionsEffect } from './roles-and-permissions/store/roles-and-permissions.effects';
import { UIEffects } from './store/effects/ui.effects';
import { OrganizationEffects } from './organization/store/organization.effects';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { DEFAULT_LANG } from './core/config/languages';
import { provideAnimations } from '@angular/platform-browser/animations';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideOptimus } from '@openng/optimus-ui/config';
import { ThemePreset } from './theme.preset';
import { apiTokenInterceptor } from './core/http/api-token.interceptor';
import { apiUrlInterceptor } from './core/http/api-url.interceptor';
import { errorHandlingInterceptorFn } from './core/http/error-handling.interceptor';
import { idempotencyInterceptor } from './core/http/idempotency.interceptor';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { EnvConfigService } from './core/env-config/env-config.service';
import { ConfirmService } from './shared/confirm/services/confirm.service';

// Local storage sync for NgRx
export function localStorageSyncReducer(reducer: ActionReducer<AppState>): ActionReducer<AppState> {
  return localStorageSync({ keys: [], rehydrate: true })(reducer);
}

const metaReducers: MetaReducer<AppState>[] = [localStorageSyncReducer];

async function appConfigInitializer() {
  const config = inject(AuthClientConfig);
  const env = inject(EnvConfigService);
  return env.load('/env.json').then((env) => config.set({
    domain: env.auth0.domain,
    clientId: env.auth0.clientId,
    authorizationParams: {
      redirect_uri: `${window.location.origin}/auth-callback`,
      audience: env.auth0.audience,
    },
    useRefreshTokens: true,
    cacheLocation: isDevMode() ? 'localstorage' : 'memory',
  }));
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideZonelessChangeDetection(),
    provideAppInitializer(appConfigInitializer),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([apiTokenInterceptor, apiUrlInterceptor, idempotencyInterceptor, errorHandlingInterceptorFn]),
    ),
    provideAuth0(),
    importProvidersFrom(NgxPermissionsModule.forRoot()),
    {
      provide: RouteReuseStrategy,
      useClass: PharRouteReuseStrategy,
    },
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    provideEffects([
      DatasetEffect,
      UserEffects,
      FormEffects,
      ProjectEffect,
      EventEffects,
      QuestionEffects,
      AssignEffects,
      SettingsEffects,
      SiteEffects,
      RolesAndPermissionsEffect,
      UIEffects,
      AuditEffects,
      OrganizationEffects,
    ]),
    provideStoreDevtools({
      maxAge: 25,
      logOnly: !environment.production,
      connectInZone: true,
    }),

    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },

    // Services
    MessageService,
    DialogService,
    ConfirmService,
    ConfirmationService,

    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json',
      }),
      fallbackLang: DEFAULT_LANG,
      lang: DEFAULT_LANG,
    }),

    provideOptimus({
      theme: {
        preset: ThemePreset,
        options: {
          darkModeSelector: false,
        },
      },
    }),
  ],
};
